import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "../motion/Reveal";
import { SOCIALS } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Thank you for reaching out.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36">
      <div className="container-editorial grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left — statement + socials */}
        <div className="lg:col-span-6">
          <Reveal>
            <p className="overline mb-6">08 — Connect</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-bone">
              Build Knowledge. <br />
              Strengthen <span className="italic text-gold">Character.</span> <br />
              Create Meaningful Progress.
            </h2>
            <p className="mt-8 text-lg text-ash leading-relaxed max-w-lg">
              Connect with Dakka Gabriel Rey to follow his perspectives on trading, personal
              development, discipline, and life transformation.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-md">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  href={s.href}
                  className="group bg-ink p-5 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-bone group-hover:text-gold transition-colors">{s.label}</span>
                    <ArrowUpRight size={15} className="text-ash group-hover:text-gold transition-colors" />
                  </div>
                  <p className="mt-1 font-mono text-[11px] text-ash truncate">{s.handle}</p>
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-6">
          <Reveal delay={0.1} y={50}>
            <form onSubmit={submit} data-testid="contact-form" className="edit-card p-8 md:p-10">
              <p className="font-serif text-2xl text-bone mb-8">Send a message</p>

              <div className="space-y-8">
                <div>
                  <label className="overline text-ash">Name</label>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="line-input mt-2"
                  />
                </div>
                <div>
                  <label className="overline text-ash">Email</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="line-input mt-2"
                  />
                </div>
                <div>
                  <label className="overline text-ash">Subject</label>
                  <input
                    data-testid="contact-subject-input"
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="What is this about?"
                    className="line-input mt-2"
                  />
                </div>
                <div>
                  <label className="overline text-ash">Message</label>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Share your thoughts…"
                    className="line-input mt-2 resize-none"
                  />
                </div>
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="btn-primary mt-10 w-full disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Connect With Dakka <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
